export const SEGMENTS = ["medical", "legal", "claims", "pm"] as const;

export type Segment = (typeof SEGMENTS)[number];

export const SEGMENT_LABELS: Record<Segment, string> = {
  medical: "Medical experts",
  legal: "Legal experts",
  claims: "Insurance & Risk experts",
  pm: "Practice management",
};

// The cross-page contract: the homepage role cards link with these slugs and
// /demo reads them back to preselect a pill. Typed so a drift cannot compile.
export const SEGMENT_BY_ROLE_SLUG = {
  "medical-expert": "medical",
  "legal-expert": "legal",
  "insurance-and-risk-expert": "claims",
  "practice-management": "pm",
} as const satisfies Record<string, Segment>;

export type RoleSlug = keyof typeof SEGMENT_BY_ROLE_SLUG;

type SegmentCopy = {
  record: string;
  dictation: string;
  report: string;
  // Legal and claims audiences don't dictate themselves — they rely on the
  // physicians who do — so the dictation panel drops out for them entirely.
  hidesDictation: boolean;
};

// The three panels, derived so a new one cannot be added here without the panel
// components failing to compile.
export type SegmentPanel = Exclude<keyof SegmentCopy, "hidesDictation">;

export const SEGMENT_COPY: Record<Segment, SegmentCopy> = {
  medical: {
    record:
      "Every page read and organized into a cited chronology, duplicates removed. Days of reading become minutes. Ask it anything and get the page number back.",
    dictation:
      "Interview the claimant with your attention on them, not on the keyboard. Every word captured and organized into your evaluation notes.",
    report:
      "Your finished report checked against your specialty’s requirements: the list a veteran evaluator keeps in their head, running on every report.",
    hidesDictation: false,
  },
  legal: {
    record:
      "Walk into deposition knowing every page: what the record supports, what it contradicts, and where.",
    dictation:
      "Used in the evaluation itself — by the physicians whose reports you rely on.",
    report:
      "Test the report against the record: strengths, weaknesses, and the gaps between opinion and evidence.",
    hidesDictation: true,
  },
  claims: {
    record:
      "Redundant records deduplicated, pre-existing conditions verified. Every finding cited to the page.",
    dictation:
      "Used in the evaluation itself — by the physicians whose reports you rely on.",
    report:
      "Does the report support the determination? Test it against the record.",
    hidesDictation: true,
  },
  pm: {
    record:
      "Every case reviewed the same way, at the same depth, across every clinician you manage. Your doctors get answers from the file instead of waiting on staff to dig for them.",
    dictation:
      "Your clinicians interview with their attention on the claimant. Every word captured into evaluation notes, on every case.",
    report:
      "A consistency check on every report before it leaves your practice, so quality doesn't depend on which doctor wrote it.",
    hidesDictation: false,
  },
};

// Operator-facing proof points, shown only to the practice-management segment.
export const PRACTICE_CHIPS = [
  "Records with QA",
  "SSO with your backend",
  "Managed by your staff",
  "BAAs signed with your practice",
];
